import { useEffect, useRef } from "react";

type Nav = (path: string) => void;

/**
 * B5.4 — vim-style "g X" navigation hotkeys.
 *
 * Press `g` followed by a letter within 800ms to navigate:
 *   g h → /        (Home)
 *   g d → /docs
 *   g c → /collections
 *   g n → /notebooks
 *   g s → /search
 *   g x → /chat    (eXchange)
 *   g r → /graph   (gRaph)
 *   g i → /highlights
 *
 * The first `g` doesn't navigate; it primes the prefix. Esc cancels.
 */
export function useGoToHotkey(navigate: Nav): void {
  const primed = useRef(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    function clearTimer() {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    }
    function reset() {
      primed.current = false;
      clearTimer();
    }
    function isEditable(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      return Boolean(target.closest("input, textarea, [contenteditable='true']"));
    }
    function onKey(e: KeyboardEvent) {
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "escape") {
        reset();
        return;
      }
      // C5 — atajo "/" foco al buscador de la página actual.
      if (k === "/" && !primed.current) {
        const input = document.querySelector<HTMLInputElement>(
          "[data-search-input], [data-testid=search-input], [data-testid=collections-q]",
        );
        if (input) {
          e.preventDefault();
          input.focus();
          input.select?.();
          return;
        }
      }
      if (!primed.current) {
        if (k === "g") {
          primed.current = true;
          clearTimer();
          timer.current = window.setTimeout(() => {
            primed.current = false;
          }, 800);
        }
        return;
      }
      // primed — consume next key
      const map: Record<string, string> = {
        h: "/",
        d: "/docs",
        c: "/collections",
        n: "/notebooks",
        s: "/search",
        x: "/chat",
        r: "/graph",
        i: "/highlights",
      };
      const path = map[k];
      reset();
      if (path) {
        e.preventDefault();
        navigate(path);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      reset();
    };
  }, [navigate]);
}
